import React, { useEffect, useState } from "react";
import { FaThumbsUp, FaThumbsDown, FaHeart, FaShare } from "react-icons/fa";
import { Button, Textarea, Card, Text, Group, Avatar, Container, ActionIcon } from "@mantine/core";
import { makePostRequest, makeDeleteRequest, makeGetRequest } from "../services/httpHelper";
import { isErrorObject } from "../utils/utils";
import ErrorHandler from "../services/ErrorHandler";
import { BlogPost } from "../types";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import { Link } from "react-router-dom";
import {  IconTrash } from "@tabler/icons-react";


interface Comment {
  id: string;
  comment: string;
  user: { id: string; first_name: string; last_name: string; avatar?: string };
  parent_id?: string | null;
  replies?: Comment[];
  commentLikes?: { user: { id: string } }[];
}

interface CommentItemProps {
  comment: Comment;
  user: any;
  handleLike: (commentId: string) => void;
  handleDelete: (commentId: string) => void;
  handleReplySubmit: (parentId: string, content: string) => void;
  isLoggedIn:boolean
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, user, handleLike, handleDelete, handleReplySubmit,isLoggedIn }) => {

  const [replyText, setReplyText] = useState("");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyTo, setReplyTo] = useState("");  // Tracks which comment is being replied to
const [newReply, setNewReply] = useState("");  // Stores the reply text

  const userLiked = comment.commentLikes?.some(like => like.user?.id === user.id);

  const renderComments = (comments:Comment[], depth = 0) => {
    return comments.map((comment) => (
      <Card key={comment.id} shadow="sm" mt="sm" style={{ marginLeft: depth * 20 }}>
        <Group>
          {comment.user?.avatar && <Avatar src={comment.user.avatar} />}
          <Text style={{ color: "#7a4806" }}>{comment.user?.first_name} {comment.user?.last_name}</Text>
        </Group>
        <Text mt="sm">{comment.comment}</Text>
  
        {/* Actions */}
        <Group mt="sm">
          <ActionIcon
            onClick={() => handleLike(comment.id)}
            color={comment?.commentLikes?.some(like => like.user?.id === user?.id) ? "green" : "gray"}
          >
            {comment?.commentLikes?.some(like => like.user?.id === user?.id) ? <FaThumbsDown /> : <FaThumbsUp />}
            {comment.commentLikes && comment.commentLikes?.length > 0 && comment.commentLikes.length}
          </ActionIcon>
  
          {isLoggedIn && user?.id === comment.user?.id && (
            <Button color="red" onClick={() => handleDelete(comment.id)}>Delete</Button>
          )}
  
          {isLoggedIn && (
            <Button variant="light" onClick={() => setReplyTo(comment.id)}>
              Reply
            </Button>
          )}
        </Group>
  
        {/* Reply Input */}
        {replyTo === comment.id && (
          <div style={{ marginLeft: "20px", marginTop: "10px" }}>
            <Textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              placeholder="Write a reply..."
            />
            <Button
              mt="sm"
              onClick={() => {
                if (newReply.trim()) {
                  handleReplySubmit(comment.id, newReply);
                  setNewReply("");
                  setReplyTo("");
                }
              }}
            >
              Submit Reply
            </Button>
          </div>
        )}
  
        {/* Render Replies Recursively */}
        {comment.replies && comment.replies?.length > 0 && (
          <div style={{ marginLeft: "20px", borderLeft: "2px solid #ddd", paddingLeft: "10px" }}>
            {renderComments(comment.replies, depth + 1)}
          </div>
        )}
      </Card>
    ));
  };
  

 
  
  
  
  return (
    <Card shadow="sm" mt="sm">
  <Group>
    {comment.user?.avatar && <Avatar src={comment.user.avatar} />}
    <Text style={{color:"#7a4806"}}>{comment.user?.first_name} {comment.user?.last_name}</Text>
  </Group>
  <Text mt="sm">{comment.comment}</Text>

  {/* Actions: Like, Delete, Reply */}
  <Group mt="sm">
    <ActionIcon onClick={() => handleLike(comment.id)}>
      {userLiked ? <FaThumbsDown color="green" /> : <FaThumbsUp />} {comment.commentLikes?.length && comment.commentLikes.length}
    </ActionIcon>

    {isLoggedIn && user?.id === comment.user?.id && (
      <Button color="red" onClick={() => handleDelete(comment.id)}><IconTrash/></Button>
    )}

    {isLoggedIn && <Button variant="light" onClick={() => setShowReplyInput(!showReplyInput)}>Reply</Button>}
  </Group>

  {/* Reply Input */}
  {showReplyInput && (
    <div style={{ marginLeft: "20px", marginTop: "10px" }}>
      <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply..." />
      <Button mt="sm" onClick={() => {
        if (replyText.trim()) {
          handleReplySubmit(comment.id, replyText);
          setReplyText("");
          setShowReplyInput(false);
        }
      }}>
        Submit Reply
      </Button>
    </div>
  )}

  {/* Render Replies Recursively */}

  {comment.replies && comment.replies?.length > 0 && (
    <div style={{ marginLeft: "20px", borderLeft: "2px solid #ddd", paddingLeft: "10px" }}>
      {renderComments(comment.replies, 1)}
    </div>
  )}
  {
 /* comment.replies && comment.replies.length > 0 && (
    <div style={{ marginLeft: "20px", borderLeft: "2px solid #ddd", paddingLeft: "10px" }}>
      {renderComments(comment.replies, user, handleLike, handleDelete, handleReplySubmit, isLoggedIn)}
    </div>
  )*/
  }
</Card>
  );
};

// Parent component that renders all comments
const PostComment: React.FC<{ post: BlogPost }> = ({ post }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [comments, setComments] = useState<Comment[]>(post?.comments || []);
  const [newComment, setNewComment] = useState("");
  const [error, setError] = useState<string | null>(null); 
   const [isLoggedIn, setIsLoggedIn] = useState(false);
  

  const navigate = useNavigate();

  const addReply = (commentList: Comment[], parentId: string, newReply: Comment): Comment[] =>
    commentList.map(comment => {
      if (comment.id === parentId) {
        return { ...comment, replies: [...(comment.replies || []), newReply] };
      }
      return { ...comment, replies: addReply(comment.replies || [], parentId, newReply) };
    });

  const handleCommentSubmit = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    if (!newComment.trim()) return;

    const res = await makePostRequest("blog/comments", { comment: newComment, post_id: post.id, parent_id: null, user_id: user.id });

    if (isErrorObject(res)) {
      setError(ErrorHandler(res));
    } else {
      setComments([...comments, res.data]);
      setNewComment("");
      setError(null);
      fetchPostComments();
    }
  };

  const handleReplySubmit = async (parentId: string, content: string) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    if (!content.trim()) return;
  
    const res = await makePostRequest("blog/comments", { comment: content, post_id: post.id, parent_id: parentId });
  
    if (isErrorObject(res)) {
      setError(ErrorHandler(res));
    } else {
      setComments(prevComments => addReply(prevComments, parentId, res.data)); // Update state without refetching
      setError(null);
      fetchPostComments()
    }
  };

  const updateLikes = (commentList: Comment[], commentId: string, newLikes: Comment["commentLikes"]): Comment[] =>
    commentList.map(comment =>
      comment.id === commentId
        ? { ...comment, commentLikes: newLikes }
        : { ...comment, replies: updateLikes(comment.replies || [], commentId, newLikes) }
    );
  

    const handleLike = async (commentId: string) => {
      const res = await makePostRequest("blog/comments/like", { post_id: post.id, comment_id: commentId, user_id: user.id });
    
      if (isErrorObject(res)) {
        setError(ErrorHandler(res));
      } else {
        setComments(prevComments => updateLikes(prevComments, commentId, res.data.likes)); // Update likes locally
        setError(null);
        fetchPostComments();
      }
    };

  const handleDelete = async (commentId: string) => {
    const res = await makeDeleteRequest(`blog/comments/${commentId}`);
    const confirmed = window.confirm('Are sure you want to delete the comment?')
    if(confirmed){
      if (isErrorObject(res)) {
        setError(ErrorHandler(res));
      } else {
        const removeComment = (commentList: Comment[]): Comment[] =>
          commentList.filter(comment => comment.id !== commentId)
            .map(comment => ({ ...comment, replies: removeComment(comment.replies || []) }));
  
        setComments(removeComment(comments));
        setError(null);
        fetchPostComments();
      }

    }

   
  };

  const fetchPostComments = async () => {
    const res = await makeGetRequest(`blog/${post?.id}/comments`);
    if (isErrorObject(res)) {
      setError(ErrorHandler(res));
    } else {
      setComments(res.data);
      setError(null);
    }
  };

  useEffect(() => {
      // Function to check if the token is expired
const tokenIsExpired = (token: string): boolean => {
  const now = Date.now();
  const decoded: { exp: number } = jwtDecode<{ exp: number }>(token);

  return decoded.exp * 1000 < now;
}
    const isUserLoggedIn=()=>{
      if(user){
        const token = sessionStorage.getItem('token');
const userLoggedIn = !token || tokenIsExpired(token) ?false:true;
setIsLoggedIn(userLoggedIn)
      }else{
        setIsLoggedIn(false)
      }
      


    }
    fetchPostComments();
    isUserLoggedIn();
  }, [post?.id,isLoggedIn]);

  return (
    <Container>
      {error && <p className="red">{error}</p>}
      {
      
      <>
        {
          isLoggedIn ? (
            <Card>
              <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Write a comment..." />
              <Button onClick={handleCommentSubmit} mt="sm">Comment</Button>
            </Card>
          )
          :
          <Text><Link to="/login">Login</Link> to comment and reply on comments</Text>
    
        }
      </>
    }

      {comments.map(comment => (
        <CommentItem 
          key={comment.id} 
          comment={comment} 
          user={user} 
          handleLike={handleLike} 
          handleDelete={handleDelete} 
          handleReplySubmit={handleReplySubmit}
          isLoggedIn={isLoggedIn} 
        />
      ))}
    </Container>
  );
};



interface PostInteractionProps {
  post: BlogPost;
}

const PostInteraction: React.FC<PostInteractionProps> = ({ post }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [likes, setLikes] = useState<number>(post?.likes.length);
  const [error, setError] = useState<string | null>(null); 

  const [userLiked, setUserLiked] = useState<boolean>(post?.likes?.some(like => like.user?.id === user?.id) || false);
  const navigate = useNavigate();


  const handleLike = async () => {
    if(!user){
      navigate('/login');
    }
    const res = await makePostRequest(`blog/post/${post.id}/like`, {
      post_id: post.id,
      user_id: user.id,
    });

    if (isErrorObject(res)) {
      console.log(error);
      setError(ErrorHandler(res));
    } else {
      setError(null);
      if(res.data.liked){
        setUserLiked(true)
        fetchPostLikes();
       

      }else{
        setUserLiked(false);
        fetchPostLikes();
      }
    }


    
  };

  const incrementShares = async () => {
    await makePostRequest(`blog-post/${post.id}/shares`, {
    
    });

  };

 const handleShare = async () => {
  try {
    await navigator.share({ title: post.title, url: window.location.href });
    // Only runs if sharing was successful
    await incrementShares();
  } catch (error) {
    console.log("User canceled sharing or an error occurred:", error);
  }
};

const fetchPostLikes = async () => {
  const res = await makeGetRequest(`blog/${post?.id}/likes`);
  if (isErrorObject(res)) {
    setError(ErrorHandler(res));
  } else {
    setLikes(res.data.length);
    setError(null);
  }
};

useEffect(()=>{
  fetchPostLikes()

},[])




  return (
    <Container mt="lg">
      <Group>
        <ActionIcon onClick={handleLike} size="lg">
          <FaHeart color={userLiked ? "red" : "gray"} />  {likes}
        </ActionIcon>
        <ActionIcon onClick={handleShare} size="lg">
          <FaShare />
        </ActionIcon>
      </Group>
    </Container>
  );
};

export { PostComment, PostInteraction };
